import {
  Group,
  Pagination,
  Paper,
  Stack,
  Table,
  Tabs,
  TextInput,
  Title,
} from '@mantine/core';
import { useMemo } from 'react';
import { useLocation } from 'wouter';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { useUrlDraft, useUrlState } from '@/hooks/useUrlState';
import { urlField } from '@/lib/urlState';
import { useContactsList, type ContactListFilters } from '@/models/contacts';
import { useHasAnyPermission } from '@/models/permissions';
import { formatDate } from '@/lib/helpers';
import { OwnerFilter } from '@/components/OwnerFilter/OwnerFilter';
import { MemberAvatarGroup } from './MembersControl';

export default function ContactListPage() {
  const canReadAll = useHasAnyPermission([
    'contacts:read-all',
    'contacts:*',
    '*:*',
  ]);
  const schema = useMemo(
    () => ({
      view: canReadAll
        ? urlField.enum(['mine', 'unassigned', 'all'] as const, 'all')
        : urlField.enum(['mine'] as const, 'mine'),
      search: urlField.string(),
      owners: urlField.numberArray(),
      page: urlField.page(),
      pageSize: urlField.number(50, { integer: true, min: 1, max: 200 }),
      sort: urlField.enum(['createdAt', 'updatedAt'] as const, 'createdAt'),
      order: urlField.enum(['asc', 'desc'] as const, 'desc'),
    }),
    [canReadAll],
  );
  const [state, setState, { clear, isFiltered }] = useUrlState(schema);
  const [searchInput, setSearchInput] = useUrlDraft(state.search, (search) =>
    setState({ search }),
  );
  const filters: ContactListFilters = {
    view: state.view,
    search: state.search || undefined,
    ownerUserId: state.owners.length ? state.owners : undefined,
    page: state.page,
    pageSize: state.pageSize,
    sort: state.sort,
    order: state.order,
  };
  const { data, isLoading } = useContactsList(filters);
  const [, navigate] = useLocation();

  return (
    <Paper p="md" h="100%">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Contacts</Title>
        </Group>

        <Tabs
          value={state.view}
          onChange={(v) => v && setState({ view: v as typeof state.view })}
        >
          <Tabs.List>
            <Tabs.Tab value="mine">My contacts</Tabs.Tab>
            {canReadAll && <Tabs.Tab value="unassigned">Unassigned</Tabs.Tab>}
            {canReadAll && <Tabs.Tab value="all">All</Tabs.Tab>}
          </Tabs.List>
        </Tabs>

        <Group>
          <TextInput
            placeholder="Search name/email/phone"
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            w={280}
          />
          <OwnerFilter
            size="sm"
            value={state.owners}
            onChange={(owners) => setState({ owners })}
            w={220}
          />
          <ClearFiltersButton visible={isFiltered} onClick={clear} />
        </Group>

        <Table
          highlightOnHover
          fz="xs"
          styles={{
            td: { whiteSpace: 'nowrap' },
            th: { whiteSpace: 'nowrap' },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Phone</Table.Th>
              <Table.Th>Members</Table.Th>
              <Table.Th>From lead</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data?.rows ?? []).map((r) => (
              <Table.Tr
                key={r.id}
                onClick={() => navigate(`~/contacts/${r.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>{r.id}</Table.Td>
                <Table.Td>
                  {[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}
                </Table.Td>
                <Table.Td>{r.email ?? '—'}</Table.Td>
                <Table.Td>{r.phone ?? '—'}</Table.Td>
                <Table.Td>
                  <MemberAvatarGroup members={r.members ?? []} />
                </Table.Td>
                <Table.Td>
                  {r.sourceLeadId ? `#${r.sourceLeadId}` : '—'}
                </Table.Td>
                <Table.Td>{formatDate(r.createdAt)}</Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && (data?.rows ?? []).length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7} align="center">
                  No contacts
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>

        <Group justify="flex-end">
          <Pagination
            total={Math.max(1, Math.ceil((data?.total ?? 0) / state.pageSize))}
            value={state.page}
            onChange={(page) => setState({ page })}
          />
        </Group>
      </Stack>
    </Paper>
  );
}
